import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/lib/legalLinks";
import "./landing.css";
import { BrandWordmark } from "./BrandWordmark";
import { FrontierWork } from "./FrontierWork";
import { Hero } from "./Hero";
import { HowItWorks } from "./HowItWorks";
import { LogoMark } from "./Navbar";
import { APP_URL } from "./siteUrls";

/**
 * The Delgentic marketing page served at the apex (delgentic.com) by CatalogStack —
 * autumn-metal variant, built to the approved preview (mission control,
 * design/home-apex.html). Final order: top (typed hero + the SchedaAd), how (Delegation,
 * not back-and-forth), own (Frontier work, on the page's light ground), contact
 * (the closing plate), footer.
 *
 * <p>The agent-card showcase and the contact form are not part of the approved design;
 * their components stay in the repo unmounted (AgentCardShowcase, ContactSection, and
 * Navbar's capsule — only LogoMark is still imported from that file). Footer stays
 * mounted by LegalPageLayout, which is how the legal pages keep their footer.
 *
 * <p>Nothing here touches a session: the apex has no OIDC provider, so both CTAs hand
 * off to the app. See {@code catalog/CatalogApp.tsx} for the invariant and its test.
 *
 * <p>`onSignup` stays in the prop type for the call sites that pass it; the approved
 * page funnels every way-in through "Try Scheda", so it is deliberately unused here.
 */
export function DelgenticLanding({
  onTryScheda,
}: {
  onTryScheda: () => void;
  onSignup: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="landing min-h-screen">
      <header className="apexnav">
        <div className="wrap navrow">
          {/* "/" not "#top": from a legal page (/privacy, /cookies) the brand must
              navigate HOME, not just scroll the current page to its top. */}
          <a href="/" className="navbrand">
            <LogoMark />
            <BrandWordmark />
          </a>
          <nav className="navmid">
            <a href="#how">{t("nav.how")}</a>
            {/* Preview wording: the mid-nav link is the short "Try Scheda"; the
                CTA capsule keeps the long "Try out Scheda". */}
            <a href={APP_URL}>{t("nav.trySchedaShort")}</a>
            <a href="#own">{t("nav.frontier")}</a>
            <a href="#contact">{t("nav.contact")}</a>
          </nav>
          <div className="navend">
            <LanguageSwitcher />
            <button type="button" className="btn cta" onClick={onTryScheda}>
              {t("nav.tryScheda")}
            </button>
          </div>
        </div>
      </header>

      <main>
        <Hero onTry={onTryScheda} />
        <HowItWorks />
        <FrontierWork />

        {/* CONTACT — the closing plate. GLOSS, 3 of 3 on this page. */}
        <section className="close" id="contact">
          <div className="wrap">
            <div className="closecard">
              <h2>{t("contact.bandTitle")}</h2>
              <p>{t("contact.bandLine")}</p>
              <div className="ctas">
                <a className="btn ghost big" href="mailto:hello@delgentic.com">
                  hello@delgentic.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="apexfoot">
        <div className="wrap">
          <div className="rule" />
          <div className="foot">
            <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
            <div className="links">
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                {t("footer.privacy")}
              </a>
              {/* Absolute for the same reason as the privacy link: Google's reviewer
                  follows it from the homepage and matches it against the URL
                  registered on the consent screen. */}
              <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">
                {t("footer.terms")}
              </a>
              <Link to="/cookies">{t("footer.cookies")}</Link>
              <a href={APP_URL}>scheda.delgentic.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
